// Copyright (c) Docugami, Inc. All rights reserved.

import { configureStore } from "@reduxjs/toolkit";
import { billingApiSlice } from "billing/BillingService";
import { createLogger } from 'redux-logger';
import { Config } from '../config';
import { apiKeysSlice } from "./apiKeys/apiKeysSlice";
import { assistedAuthoringApi, assistedAuthoringSlice } from "./assistedauthinfo/assistedAuthoringSlice";
import { authenticationReducer } from './authentication/reducers';
import { chatApi, chatSlice } from "./chat/chatSlice";
import { clustersReducer } from './clusters/reducers';
import { samplesApiSlice } from "./clusters/samplesSlice";
import { dgmlSlice } from "./dgml/dgmlSlice";
import { displayReducer } from './display/reducers';
import { documentsReducer } from './documents/reducers';
import { errorSlice } from "./errorSlice";
import { featureFlagsReducer } from './featureFlags/reducers';
import { frontendSlice } from "./frontend/frontendSlice";
import { listenerMiddleware } from "./listenerMiddleware";
import { organizationApiSlice } from "./organization/organizationSlice";
import { repeatsSlice } from "./repeatsSlice";
import { organizationReducer } from './organization/reducers';
import { reportOperationsSlice, reportsApiSlice, reportsSlice } from "./reports/reportsSlice";
import { sdvSlice } from "./sdvSlice";
import { settingsReducer } from './settings/reducers';
import { urlHashParametersReducer } from './urlhashparameters/reducers';
import { auth0UserApiSlice } from "./user/auth0UserSlice";
import { workPathsApi, workPathsSlice } from "./work_path/workPathSlice";

export const store = configureStore({
    reducer: {
        authentication: authenticationReducer,
        clusters: clustersReducer,
        documents: documentsReducer,
        settings: settingsReducer,
        [assistedAuthoringSlice.name]: assistedAuthoringSlice.reducer,
        [assistedAuthoringApi.reducerPath]: assistedAuthoringApi.reducer,
        organizations: organizationReducer,
        urlHashParameters: urlHashParametersReducer,
        featureFlags: featureFlagsReducer,
        display: displayReducer,
        [reportsApiSlice.reducerPath]: reportsApiSlice.reducer,
        [reportOperationsSlice.name]: reportOperationsSlice.reducer,
        [workPathsSlice.name]: workPathsSlice.reducer,
        [workPathsApi.reducerPath]: workPathsApi.reducer,
        [billingApiSlice.reducerPath]: billingApiSlice.reducer,
        [auth0UserApiSlice.reducerPath]: auth0UserApiSlice.reducer,
        [apiKeysSlice.reducerPath]: apiKeysSlice.reducer,
        [chatApi.reducerPath]: chatApi.reducer,
        [chatSlice.name]: chatSlice.reducer,
        [dgmlSlice.reducerPath]: dgmlSlice.reducer,
        [frontendSlice.reducerPath]: frontendSlice.reducer,
        [sdvSlice.name]: sdvSlice.reducer,
        [samplesApiSlice.reducerPath]: samplesApiSlice.reducer,
        [errorSlice.name]: errorSlice.reducer,
        [reportsSlice.name]: reportsSlice.reducer,
        [organizationApiSlice.reducerPath]: organizationApiSlice.reducer,
        [repeatsSlice.reducerPath]: repeatsSlice.reducer
    },
    // TODO: Please re-enable immutable check when we clean-up our redux usage
    middleware: gDM => gDM({ immutableCheck: false })
        .prepend(listenerMiddleware.middleware)
        .concat(reportsApiSlice.middleware)
        .concat(billingApiSlice.middleware)
        .concat(auth0UserApiSlice.middleware)
        .concat(apiKeysSlice.middleware)
        .concat(assistedAuthoringApi.middleware)
        .concat(dgmlSlice.middleware)
        .concat(chatApi.middleware)
        .concat(frontendSlice.middleware)
        .concat(samplesApiSlice.middleware)
        .concat(organizationApiSlice.middleware)
        .concat(repeatsSlice.middleware)
        .concat(workPathsApi.middleware)
        .concat(Config.verboseReduxLogging ? [createLogger()] : [])
});

export type AppState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
